import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Database, Plus, Trash2, ExternalLink, RefreshCw, Plug, ToggleLeft, ToggleRight,
  CheckCircle2, XCircle, Info, Sparkles, FileBox,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const KIND_LABELS = {
  procesos: "Procesos",
  contratos: "Contratos",
  adjudicaciones: "Adjudicaciones",
  convenios: "Convenios",
  ordenes_compra: "Órdenes de compra",
  plan_anual: "Plan anual",
  otro: "Otro",
};

const SOURCE_BADGE = {
  "SECOP II": "bg-indigo-50 border-indigo-200 text-indigo-700",
  "SECOP I": "bg-amber-50 border-amber-200 text-amber-700",
  "TVEC": "bg-emerald-50 border-emerald-200 text-emerald-700",
  "PAA": "bg-sky-50 border-sky-200 text-sky-700",
  "Otro": "bg-gray-50 border-gray-200 text-gray-600",
};

/**
 * Sección "Datasets SECOP / datos.gov.co".
 * Registry curado y editable de datasets Socrata para análisis y
 * relacionamiento completo (procesos, contratos, adjudicaciones,
 * convenios, TVEC, PAA, etc.).
 */
export default function SecopDatasetsSection() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({
    label: "", dataset_id: "", source: "SECOP II", kind: "procesos",
    value_field: "", description: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/secop/datasets");
      setItems(data.items || []);
      setTotals(data.totals || {});
      setFacets(data.facets || {});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar datasets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  const seedCurated = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/secop/datasets/seed-curated");
      if (data.inserted) toast.success(`${data.inserted} datasets curados agregados`);
      else toast.info("El catálogo ya estaba sembrado");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al sembrar");
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  const toggleEnabled = useCallback(async (it) => {
    setBusyId(it.id);
    try {
      await api.patch(`/admin/secop/datasets/${it.id}`, { enabled: !it.enabled });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  const deleteDataset = useCallback(async (it) => {
    if (it.canonical) {
      toast.error("Los datasets canónicos no se pueden eliminar (solo deshabilitar).");
      return;
    }
    if (!window.confirm(`¿Eliminar el dataset "${it.label}" (${it.dataset_id})?`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/admin/secop/datasets/${it.id}`);
      toast.success("Dataset eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo eliminar");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  const testDataset = useCallback(async (it) => {
    setBusyId(it.id);
    try {
      const { data } = await api.post(`/admin/secop/datasets/${it.id}/test`);
      if (data.ok) toast.success(`${it.label}: ${data.items_returned} item(s) · ${data.latency_ms}ms`);
      else toast.error(`${it.label}: ${String(data.error || "falló").slice(0, 120)}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al probar dataset");
    } finally {
      setBusyId(null);
    }
  }, [refresh]);

  const addNew = useCallback(async () => {
    const payload = {
      label: draft.label.trim(),
      dataset_id: draft.dataset_id.trim().toLowerCase(),
      source: draft.source,
      kind: draft.kind,
      value_field: draft.value_field.trim() || null,
      description: draft.description.trim(),
    };
    if (!payload.label || !payload.dataset_id) {
      toast.error("Etiqueta y dataset_id son obligatorios");
      return;
    }
    setBusyId("new");
    try {
      await api.post("/admin/secop/datasets", payload);
      toast.success("Dataset registrado");
      setDraft({ label: "", dataset_id: "", source: "SECOP II", kind: "procesos", value_field: "", description: "" });
      setShowAdd(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo registrar");
    } finally {
      setBusyId(null);
    }
  }, [draft, refresh]);

  const enabledCount = totals.enabled ?? 0;
  const isEmpty = !loading && items.length === 0;

  // Agrupar por source para presentar visualmente
  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const k = it.source || "Otro";
      if (!map[k]) map[k] = [];
      map[k].push(it);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div className="border border-gray-200 bg-white" data-testid="secop-datasets-section">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Database size={14} className="text-emerald-600" />
          <h2 className="font-serif text-base">Datasets SECOP / datos.gov.co</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-500">
            registry para análisis y relacionamiento completo
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isEmpty && (
            <Button
              type="button"
              variant="outline"
              onClick={seedCurated}
              disabled={loading}
              className="rounded-sm h-8 text-xs"
              data-testid="secop-datasets-seed-btn"
            >
              <Sparkles size={13} className="mr-1.5 text-amber-600" />
              Sembrar catálogo curado
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowAdd((v) => !v)}
            className="rounded-sm h-8 text-xs"
            data-testid="secop-datasets-add-toggle"
          >
            <Plus size={13} className="mr-1.5" />
            {showAdd ? "Cerrar" : "Agregar dataset"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={refresh}
            disabled={loading}
            className="rounded-sm h-8 px-2"
            data-testid="secop-datasets-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {/* KPIs + facets */}
      <div className="px-4 py-2 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-gray-100 text-xs">
        <KpiInline label="Datasets" value={totals.count ?? 0} testid="secop-datasets-kpi-count" />
        <KpiInline label="Habilitados" value={enabledCount} testid="secop-datasets-kpi-enabled" />
        <KpiInline label="Canónicos" value={totals.canonical ?? 0} testid="secop-datasets-kpi-canonical" />
        <KpiInline
          label="Tipos"
          value={(facets.by_kind || []).length}
          testid="secop-datasets-kpi-kinds"
        />
      </div>

      {/* Banner info */}
      <div className="px-4 py-2 bg-emerald-50/50 border-b border-emerald-100 text-[11px] text-gray-700 flex items-start gap-2">
        <Info size={13} className="text-emerald-700 mt-0.5 shrink-0" />
        <span>
          Los datasets <strong>canónicos</strong> (procesos + contratos SECOP II) se usan hoy en las búsquedas
          del módulo de comparables. Los demás están registrados como referencia y para futuras integraciones
          (búsqueda multi-dataset, agregación de TVEC/PAA, histórico SECOP I).
        </span>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/40" data-testid="secop-datasets-add-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Etiqueta</label>
              <Input
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="Ej: SECOP II — Comprobantes"
                className="h-8 text-xs rounded-sm"
                maxLength={120}
                data-testid="secop-dataset-input-label"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Dataset ID (xxxx-xxxx)</label>
              <Input
                value={draft.dataset_id}
                onChange={(e) => setDraft({ ...draft, dataset_id: e.target.value })}
                placeholder="p6dx-8zbt"
                className="h-8 text-xs rounded-sm font-mono"
                maxLength={9}
                data-testid="secop-dataset-input-id"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Campo de valor (opcional)</label>
              <Input
                value={draft.value_field}
                onChange={(e) => setDraft({ ...draft, value_field: e.target.value })}
                placeholder="precio_base, valor_del_contrato…"
                className="h-8 text-xs rounded-sm font-mono"
                maxLength={80}
                data-testid="secop-dataset-input-value-field"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Fuente</label>
              <select
                value={draft.source}
                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                className="w-full h-8 px-2 text-xs border border-gray-300 rounded-sm bg-white"
                data-testid="secop-dataset-input-source"
              >
                {["SECOP II", "SECOP I", "TVEC", "PAA", "Otro"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Tipo</label>
              <select
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                className="w-full h-8 px-2 text-xs border border-gray-300 rounded-sm bg-white"
                data-testid="secop-dataset-input-kind"
              >
                {Object.entries(KIND_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Descripción</label>
              <Input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Para qué sirve y qué relaciones permite analizar"
                className="h-8 text-xs rounded-sm"
                maxLength={500}
                data-testid="secop-dataset-input-description"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              onClick={addNew}
              disabled={busyId === "new" || !draft.label || !draft.dataset_id}
              className="rounded-sm h-8 text-xs"
              data-testid="secop-dataset-add-btn"
            >
              <Plus size={13} className="mr-1.5" />
              Registrar dataset
            </Button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="p-6 text-center text-sm text-gray-500" data-testid="secop-datasets-empty">
          <FileBox size={24} className="mx-auto text-gray-300 mb-2" />
          Aún no hay datasets registrados. Usa &ldquo;Sembrar catálogo curado&rdquo; para empezar con los 7 datasets
          oficiales conocidos (SECOP I/II, TVEC, PAA, Adjudicaciones).
        </div>
      )}

      {/* Lista agrupada por fuente */}
      {grouped.map(([source, list]) => (
        <div key={source} className="border-t border-gray-100" data-testid={`secop-datasets-group-${source}`}>
          <div className="px-4 py-1.5 bg-gray-50 text-[10px] uppercase tracking-widest text-gray-600 flex items-center gap-2">
            <span className={`px-1.5 py-0.5 border rounded-sm ${SOURCE_BADGE[source] || SOURCE_BADGE["Otro"]}`}>
              {source}
            </span>
            <span>{list.length} dataset(s)</span>
          </div>
          {list.map((it, i) => (
            <div
              key={it.id}
              className="px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 border-t border-gray-50 hover:bg-gray-50/40 text-xs"
              data-testid={`secop-dataset-row-${source}-${i}`}
            >
              <div className="md:col-span-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-gray-800">{it.label}</span>
                  {it.canonical && (
                    <span className="px-1 py-0.5 text-[9px] bg-indigo-100 text-indigo-700 rounded-sm">Canónico</span>
                  )}
                  {!it.enabled && (
                    <span className="px-1 py-0.5 text-[9px] bg-gray-200 text-gray-700 rounded-sm">Off</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">{it.description}</div>
              </div>
              <div className="md:col-span-2 font-mono text-[11px] text-gray-700 flex items-center gap-1">
                {it.dataset_id}
                <a
                  href={`https://www.datos.gov.co/d/${it.dataset_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-400 hover:text-primary"
                  title="Abrir en datos.gov.co"
                  data-testid={`secop-dataset-link-${source}-${i}`}
                >
                  <ExternalLink size={10} />
                </a>
              </div>
              <div className="md:col-span-2 text-gray-600">
                <span className="text-[10px] uppercase tracking-wide text-gray-400">tipo:</span> {KIND_LABELS[it.kind] || it.kind}
                {it.value_field && (
                  <div className="text-[10px] text-gray-500 font-mono mt-0.5">{it.value_field}</div>
                )}
              </div>
              <div className="md:col-span-2 text-[11px]">
                {it.last_tested_at ? (
                  <div className="flex items-center gap-1">
                    {it.last_test_ok ? (
                      <CheckCircle2 size={11} className="text-emerald-600" />
                    ) : (
                      <XCircle size={11} className="text-rose-600" />
                    )}
                    <span className="text-gray-500">
                      {new Date(it.last_tested_at).toLocaleDateString("es-CO")}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400 italic">sin probar</span>
                )}
                {it.last_test_error && (
                  <div className="text-[10px] text-rose-600 truncate" title={it.last_test_error}>
                    {String(it.last_test_error).slice(0, 60)}
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex items-center justify-end gap-1">
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => testDataset(it)}
                  disabled={busyId === it.id}
                  className="h-7 px-2"
                  title="Probar"
                  data-testid={`secop-dataset-test-${source}-${i}`}
                >
                  <Plug size={11} />
                </Button>
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => toggleEnabled(it)}
                  disabled={busyId === it.id}
                  className="h-7 px-2"
                  title={it.enabled ? "Deshabilitar" : "Habilitar"}
                  data-testid={`secop-dataset-toggle-${source}-${i}`}
                >
                  {it.enabled
                    ? <ToggleRight size={13} className="text-emerald-600" />
                    : <ToggleLeft size={13} className="text-gray-500" />}
                </Button>
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => deleteDataset(it)}
                  disabled={busyId === it.id || it.canonical}
                  className={`h-7 px-2 ${it.canonical ? "opacity-30 cursor-not-allowed" : "text-rose-600 hover:bg-rose-50"}`}
                  title={it.canonical ? "Canónico — no se puede eliminar" : "Eliminar"}
                  data-testid={`secop-dataset-delete-${source}-${i}`}
                >
                  <Trash2 size={11} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function KpiInline({ label, value, testid, warn = false }) {
  return (
    <div className="flex items-center justify-between border border-gray-200 bg-gray-50/40 px-2 py-1.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-gray-500">{label}</span>
      <span className={`font-mono tabular-nums ${warn ? "text-rose-600 font-medium" : "text-gray-800"}`}>{value}</span>
    </div>
  );
}
